import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { sharedFontStyles } from '../DolEditor/font-styles';
import { EditorProps } from '../DolEditor/types';
import renderNode, { Div } from '../renderNode';
import { withStoreRequiredForEditor } from './core/withStoreRequiredForEditor';
import { DictionaryBlogEditorContextMenuPresets } from './plugins/BlogEditorToolbar/BlogEditorContextMenu.presets';
import { SideBarOnEditorFocusedProps } from './SideBarOnEditorFocused';
import { useTextOnlyEditorPlugin } from './TextOnlyEditorPlugin.presets';

const BlogEditorBody = dynamic<any>(import('./BlogEditor').then((mod) => mod.BlogEditorBody));
const BlogEditorReadOnly = dynamic<any>(
  import('./BlogEditorReadOnly').then((mod) => mod.BlogEditorReadOnly)
);

const BlogEditorRightToolbar = dynamic<any>(
  () =>
    import('./plugins/BlogEditorToolbar/BlogEditorRightToolbar').then(
      (mod) => mod.BlogEditorRightToolbar
    ),
  { ssr: false }
);

const SideBarOnEditorFocused = dynamic<any>(
  () => import('./SideBarOnEditorFocused').then((mod) => mod.SideBarOnEditorFocused),
  { ssr: false }
);

const Main = styled.div`
  .dol-editor {
    background-color: ${colorsV2.gray10};
    padding: 16px;
    ${sharedFontStyles};
    font-size: 14px;
    line-height: 20px;
  }
`;

export interface TextOnlyEditorProps
  extends EditorProps,
    Omit<SideBarOnEditorFocusedProps, 'children'> {
  isReadOnly?: boolean;
  renderTopBar?: () => JSX.Element;
  placeHolder?: string;
  overrideToolbar?: any[];
  topOveride?: number;
}

export const TextOnlyEditorBase = observer((props: TextOnlyEditorProps) => {
  const {
    isReadOnly,
    renderTopBar,
    stickDirection,
    stickSpacing,
    placeHolder,
    overrideToolbar,
    topOveride,
    ...rest
  } = props;
  const { plugins, preprocessReadOnlyInitialValues } = useTextOnlyEditorPlugin({
    isReadOnly,
    overrideRightToolbar: overrideToolbar,
  });
  return (
    <Main className={cl('text-only-editor-container', { 'read-only': isReadOnly })}>
      <div className="text-only-editor-body">
        {renderNode(Div, renderTopBar, {})}
        {isReadOnly ? (
          <BlogEditorReadOnly
            placeholder={placeHolder}
            contextMenuButtons={DictionaryBlogEditorContextMenuPresets}
            plugins={plugins}
            preprocessReadOnlyInitialValues={preprocessReadOnlyInitialValues}
            {...rest}
          />
        ) : (
          <BlogEditorBody
            placeholder={placeHolder}
            contextMenuButtons={DictionaryBlogEditorContextMenuPresets}
            plugins={plugins}
            {...rest}
          />
        )}
      </div>
      {!isReadOnly && (
        <SideBarOnEditorFocused
          topOveride={topOveride}
          stickSpacing={stickSpacing}
          stickDirection={stickDirection}
        >
          <BlogEditorRightToolbar overrideToolbar={overrideToolbar} />
        </SideBarOnEditorFocused>
      )}
    </Main>
  );
});

export const TextOnlyEditor: React.FC<TextOnlyEditorProps> = compose(
  withStoreRequiredForEditor({})
)(TextOnlyEditorBase);
